import { useEffect } from "react";
import { ArrayInput, FormDataConsumer, SimpleFormIterator, useDataProvider } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import Inputs from "./Inputs";

export default function BonusSaleItemForm() {
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const saleItems = useWatch({ name: "sale_items" });
  const bonusSaleItems = useWatch({ name: "bonus_sale_items" });
  const saleOrderId = useWatch({ name: "sale_order_id" });

  useEffect(() => {
    if (!saleOrderId || !saleItems?.length) {
      return;
    }

    const existingRows = bonusSaleItems?.filter(
      (row) => row && (row.sale_order_item_id || row.bonus_product_id || row.sent_quantity)
    );
    const shouldPopulateFromSaleItems = !existingRows?.length;
    // A row with only its main product selected is still being edited.  Do not
    // try to resolve its name until a bonus product has also been selected;
    // otherwise the setValue below rewrites the array on every render.
    const needsNameFill = existingRows?.some(
      (row) => row?.bonus_product_id && !row.name
    );
    if (!shouldPopulateFromSaleItems && !needsNameFill) {
      return;
    }

    const rowsToFill = shouldPopulateFromSaleItems
      ? saleItems
          .filter((item) => item?.bonus_quantity > 0)
          .map((item) => ({
            sale_order_item_id: item.sale_order_item_id,
            bonus_product_id: item.product_id,
            sent_quantity: item.bonus_quantity,
            name: "",
          }))
      : existingRows;

    if (rowsToFill.length === 0) {
      return;
    }

    const loadNames = async () => {
      try {
        const { data } = await dataProvider.getList("sale-order-items", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "id", order: "ASC" },
          filter: { sale_order_id: saleOrderId },
        });

        const nameMap = data.reduce((acc, item) => {
          if (item.product_id) {
            acc[item.product_id] = item.name || acc[item.product_id] || "";
          }
          return acc;
        }, {});

        const filled = rowsToFill.map((row) => ({
          ...row,
          name: row.name || nameMap[row.bonus_product_id] || "",
        }));

        // Avoid publishing an identical value back to react-hook-form.  An
        // unnecessary write causes ReferenceInput to refetch its choices and
        // makes the main-product dropdown appear to blink.
        const namesChanged = filled.some(
          (row, index) => row.name !== rowsToFill[index]?.name
        );
        if (shouldPopulateFromSaleItems || namesChanged) {
          setValue("bonus_sale_items", filled, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      } catch (error) {
        if (shouldPopulateFromSaleItems) {
          setValue("bonus_sale_items", rowsToFill, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      }
    };

    loadNames();
  }, [saleOrderId, saleItems, bonusSaleItems, dataProvider, setValue]);

  return (
    <ArrayInput source="bonus_sale_items">
      <SimpleFormIterator
        disableReordering
        getItemLabel={(index) => `#${index + 1}`}
        fullWidth={true}
        sx={{
          "& .RaSimpleFormIterator-line": {
            border: "none",
          },
        }}
      >
        <FormDataConsumer>{Inputs}</FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  );
}
